import React, {createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useState} from "react";


interface RefreshControllerContextType {
  refresh: (key: string) => void
  latestRefreshMap: {[key: string]: number}
}

const RefreshControllerContext = createContext({} as RefreshControllerContextType)

const RefreshControllerContextProvider: FC<{children: ReactNode}> = ({children}) => {
  const [latestRefresh, setLatestRefresh] = useState<{[key: string]: number}>({})
  const refresh = useCallback((key: string) => {
    setLatestRefresh(old => ({...old, [key]: (new Date()).getTime()}))
  }, [])
  return <RefreshControllerContext.Provider value={{
    refresh,
    latestRefreshMap: latestRefresh,
  }}>{children}</RefreshControllerContext.Provider>
}

/**
 * This component should be placed at the top of the component tree
 * @param props
 * @constructor
 */
export const RefreshController: FC<{children: ReactNode}> = props => {
  return <RefreshControllerContextProvider>
    {props.children}
  </RefreshControllerContextProvider>
}

/**
 * Works like useEffect, but only runs when the refresh function is called
 */
export const useRefreshEffect = (key: string, effect: () => void, dependencies: any[] = []) => {
  const latestRefresh = useLatestRefreshTime(key)
  useEffect(() => {
    effect()
  }, [latestRefresh, ...dependencies])
}

/**
 * Returns the refresh function, this will refresh all components that use useRefreshEffect
 */
export const useRefresh = () => {
  const {refresh} = useContext(RefreshControllerContext)
  return refresh
}

/**
 * Returns latest refresh moment, this can be used to manually listen to refresh events.
 */
export const useLatestRefreshTime = (key: string) => {
  const {latestRefreshMap} = useContext(RefreshControllerContext)
  return useMemo(() => {
    return latestRefreshMap[key] ?? 0
  }, [latestRefreshMap, key])
}